import { API } from '@/lib/api';
import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Truck, Users, Clock, Shield, BarChart3, CheckCircle2, ArrowRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import axios from "axios";



const industries = [
  "Furniture & Home Goods",
  "E-commerce / Retail",
  "Appliances & Electronics",
  "Construction & Building Materials",
  "Grocery & Food Service",
  "Healthcare & Medical",
  "Manufacturing",
  "Other"
];

const volumes = [
  "1-10 deliveries/month",
  "11-50 deliveries/month",
  "51-200 deliveries/month",
  "200+ deliveries/month"
];

const B2BPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    contact_person: "",
    industry_type: "",
    monthly_volume: "",
    phone: "",
    email: ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.contact_person || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/b2b/lead`, formData);
      setSubmitted(true);
      toast.success("Your request has been submitted!");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Clock,
      title: "Same-Day & Scheduled",
      description: "Flexible delivery windows that work for your business and customers."
    },
    {
      icon: Users,
      title: "Driver + Helper Bundles",
      description: "Scale up with helpers for furniture, appliances, and heavy items."
    },
    {
      icon: BarChart3,
      title: "Volume Pricing",
      description: "Flat-rate or tiered pricing based on your delivery volume."
    },
    {
      icon: Shield,
      title: "Proof of Delivery",
      description: "Photo documentation and e-signatures for every delivery."
    },
    {
      icon: Truck,
      title: "No Fleet Required",
      description: "Tap into our network of 500+ vetted local drivers."
    },
    {
      icon: Building2,
      title: "Dedicated Support",
      description: "Account manager and priority support for your business."
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="b2b-page">
      <Navbar />

      {/* Hero */}
      <section className="hero-bg pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="caption text-lime-400 mb-4 block">For Businesses</span>
              <h1 className="heading-1 text-white mb-6">
                Scale Deliveries Without Trucks, Drivers, or Payroll
              </h1>
              <p className="body-lg text-stone-300 mb-8">
                Whether you're a furniture store, e-commerce business, or local retailer, Stack handles your delivery operations end-to-end.
              </p>
              <div className="flex flex-wrap gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-lime-400" />
                  200+ Business Partners
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-lime-400" />
                  24-Hour Quote Turnaround
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/6068761/pexels-photo-6068761.jpeg"
                alt="Business Logistics"
                className="rounded-3xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-spacing" data-testid="b2b-features">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption text-emerald-600 mb-4 block">Why Stack</span>
            <h2 className="heading-2 text-emerald-950">Everything Your Business Needs</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200 rounded-3xl overflow-hidden">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-950 mb-2">{feature.title}</h3>
                <p className="text-stone-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section className="section-spacing bg-emerald-950 relative" data-testid="b2b-form-section">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="caption text-lime-400 mb-4 block">Get Started</span>
              <h2 className="heading-2 text-white mb-6">
                Get a Custom Quote in 24 Hours
              </h2>
              <p className="body-lg text-stone-400 mb-8">
                Tell us about your delivery needs and we'll create a tailored solution for your business.
              </p>

              <div className="space-y-6">
                {[
                  "Dedicated account manager",
                  "Custom pricing based on volume",
                  "API integration available",
                  "Same-day onboarding possible"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-950" />
                    </div>
                    <span className="text-stone-300">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {!submitted ? (
                <form onSubmit={handleSubmit} className="glass-card p-8" data-testid="b2b-lead-form">
                  <h3 className="text-xl font-semibold text-emerald-950 mb-6">Request a Quote</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <Label className="text-emerald-950 font-medium mb-2 block">Business Name *</Label>
                      <Input
                        className="input-style"
                        placeholder="Your Company Inc."
                        value={formData.business_name}
                        onChange={(e) => handleInputChange("business_name", e.target.value)}
                        data-testid="business-name-input"
                      />
                    </div>

                    <div>
                      <Label className="text-emerald-950 font-medium mb-2 block">Contact Person *</Label>
                      <Input
                        className="input-style"
                        placeholder="Jane Smith"
                        value={formData.contact_person}
                        onChange={(e) => handleInputChange("contact_person", e.target.value)}
                        data-testid="contact-person-input"
                      />
                    </div>

                    <div>
                      <Label className="text-emerald-950 font-medium mb-2 block">Industry Type</Label>
                      <Select
                        value={formData.industry_type}
                        onValueChange={(value) => handleInputChange("industry_type", value)}
                      >
                        <SelectTrigger className="input-style" data-testid="industry-type-select">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((industry) => (
                            <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-emerald-950 font-medium mb-2 block">Monthly Delivery Volume</Label>
                      <Select
                        value={formData.monthly_volume}
                        onValueChange={(value) => handleInputChange("monthly_volume", value)}
                      >
                        <SelectTrigger className="input-style" data-testid="monthly-volume-select">
                          <SelectValue placeholder="Select volume" />
                        </SelectTrigger>
                        <SelectContent>
                          {volumes.map((volume) => (
                            <SelectItem key={volume} value={volume}>{volume}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-emerald-950 font-medium mb-2 block">Phone *</Label>
                        <Input
                          className="input-style"
                          placeholder="(416) 555-0123"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          data-testid="phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-emerald-950 font-medium mb-2 block">Email *</Label>
                        <Input
                          className="input-style"
                          type="email"
                          placeholder="jane@company.com"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="btn-primary w-full"
                      disabled={loading}
                      data-testid="submit-b2b-btn"
                    >
                      {loading ? "Submitting..." : "Get a Custom Quote"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="glass-card p-8 text-center"
                  data-testid="b2b-success"
                >
                  <div className="w-20 h-20 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send className="w-10 h-10 text-emerald-950" />
                  </div>
                  <h3 className="text-2xl font-semibold text-emerald-950 mb-4">Request Received!</h3>
                  <p className="text-stone-600 mb-6">
                    Thank you for your interest in Stack for Business. Our team will contact you within 24 hours with a custom quote.
                  </p>
                  <div className="bg-stone-100 rounded-xl p-4 text-left">
                    <p className="text-sm text-stone-500 mb-1">What's next?</p>
                    <ul className="text-stone-700 space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        You'll receive a confirmation email
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        Our sales team will review your needs
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        Expect a call within 24 hours
                      </li>
                    </ul>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="text-6xl text-lime-400 mb-6">"</div>
            <blockquote className="text-2xl md:text-3xl text-emerald-950 font-medium mb-8 leading-relaxed">
              Stack transformed our delivery operations. We went from managing our own fleet to having reliable same-day delivery without the overhead.
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="font-semibold text-emerald-600">MF</span>
              </div>
              <div className="text-left">
                <p className="font-semibold text-emerald-950">Michael Foster</p>
                <p className="text-stone-500">CEO, Ontario Furniture Co.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default B2BPage;
